import { useMemo, useState } from "react";
import {
  Stack,
  Title,
  Text,
  Group,
  Paper,
  ActionIcon,
  Button,
  Checkbox,
  Badge,
  SegmentedControl,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconFileZip, IconTrash } from "@tabler/icons-react";
import { DataTable } from "../../DataTable";

export function ZIPUploadStep({
  zipFile,
  onZipDrop,
  onRemoveZip,
  onUpload,
  onPrev,
  isPending,
  shouldAutoCreateAnnouncement,
  onToggleShouldAutoCreateAnnouncement,
  shouldEmailRecipients,
  onToggleShouldEmailRecipients,
  zipInspection,
}) {
  const [filter, setFilter] = useState("all");

  const missingCount = useMemo(
    () => (zipInspection ? zipInspection.total - zipInspection.found : 0),
    [zipInspection]
  );

  const filteredRecords = useMemo(() => {
    if (!zipInspection) return [];
    if (filter === "found") return zipInspection.records.filter((r) => r.inZip);
    if (filter === "missing")
      return zipInspection.records.filter((r) => !r.inZip);
    return zipInspection.records;
  }, [zipInspection, filter]);

  return (
    <Stack gap="md">
      <Title order={3}>Upload ZIP Archive</Title>
      <Text size="sm" c="dimmed">
        Upload a ZIP file containing all PDFs referenced in the manifest.
      </Text>

      {!zipFile ? (
        <Dropzone
          onDrop={onZipDrop}
          maxFiles={1}
          maxSize={100 * 1024 * 1024} // 100MB limit
          accept={["application/zip", "application/x-zip-compressed"]}
        >
          <Group
            justify="center"
            gap="xl"
            mih={220}
            style={{ pointerEvents: "none" }}
          >
            <IconFileZip size="3.2rem" stroke={1.5} />

            <div>
              <Text size="xl" inline>
                Drag ZIP file here or click to select
              </Text>
              <Text size="sm" c="dimmed" inline mt={7}>
                PDF files inside the ZIP will be matched by filename
              </Text>
            </div>
          </Group>
        </Dropzone>
      ) : (
        <Paper withBorder p="md" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between">
            <Group gap="md">
              <IconFileZip size="1.5rem" color="blue" />
              <div>
                <Text fw={500}>{zipFile.name}</Text>
                <Text size="xs" c="dimmed">
                  {(zipFile.size / 1024).toFixed(1)} KB
                </Text>
              </div>
            </Group>
            <ActionIcon color="red" variant="subtle" onClick={onRemoveZip}>
              <IconTrash size="1.2rem" />
            </ActionIcon>
          </Group>
        </Paper>
      )}

      <Checkbox
        label="Create announcement automatically after successful upload"
        checked={shouldAutoCreateAnnouncement}
        onChange={(event) =>
          onToggleShouldAutoCreateAnnouncement(event.currentTarget.checked)
        }
      />

      <Checkbox
        label="Send emails"
        description="Email each recipient a link that opens their document without signing in"
        checked={shouldEmailRecipients}
        onChange={(event) =>
          onToggleShouldEmailRecipients(event.currentTarget.checked)
        }
      />

      {zipInspection && (
        <>
          <Group grow>
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderLeft: "4px solid var(--mantine-color-green-filled)",
              }}
            >
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Documents Found
              </Text>
              <Text size="xl" fw={700}>
                {zipInspection.found} / {zipInspection.total}
              </Text>
            </Paper>
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderLeft: `4px solid var(--mantine-color-${missingCount > 0 ? "orange" : "green"}-filled)`,
              }}
            >
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Missing in ZIP
              </Text>
              <Text size="xl" fw={700}>
                {missingCount}
              </Text>
            </Paper>
          </Group>

          <DataTable
            columns={[
              {
                accessor: "filename",
              },
              {
                accessor: "status",
                filter: (
                  <SegmentedControl
                    value={filter}
                    onChange={setFilter}
                    size="xs"
                    data={[
                      { label: "All", value: "all" },
                      { label: "Found", value: "found" },
                      { label: "Missing", value: "missing" },
                    ]}
                  />
                ),
                filtering: filter !== "all",
                render: (r) =>
                  r.inZip ? (
                    <Badge color="green">Found</Badge>
                  ) : (
                    <Badge color="orange">Missing</Badge>
                  ),
              },
            ]}
            records={filteredRecords}
            withTableBorder={true}
            withColumnBorders={true}
            striped
            height={300}
          />
        </>
      )}

      <Group justify="space-between" mt="xl">
        <Button variant="default" onClick={onPrev}>
          Back
        </Button>
        <Button onClick={onUpload} disabled={!zipFile} loading={isPending}>
          Upload
        </Button>
      </Group>
    </Stack>
  );
}
